import type { AvailabilitySlot, ManagePayload } from './types';

const API = import.meta.env.VITE_BOOKING_API_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!API || !ANON) {
  // eslint-disable-next-line no-console
  console.warn('manage: VITE_BOOKING_API_URL or VITE_SUPABASE_ANON_KEY missing');
}

interface ManageError {
  error: string;
  message?: string;
  lifecycle?: string;
}

async function manageCall<T>(body: Record<string, unknown>): Promise<{ ok: true; data: T } | { ok: false; error: ManageError; status: number }> {
  let res: Response;
  try {
    res = await fetch(`${API}/booking-manage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: ANON,
        Authorization: `Bearer ${ANON}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      status: 0,
      error: { error: 'network_error', message: e instanceof Error ? e.message : String(e) },
    };
  }

  let json: unknown = null;
  try { json = await res.json(); } catch { /* ignore */ }

  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      error: (json as ManageError) ?? { error: 'unknown_error', message: `HTTP ${res.status}` },
    };
  }
  return { ok: true, data: json as T };
}

export function fetchBooking(token: string) {
  return manageCall<ManagePayload>({ action: 'fetch', manage_token: token });
}

// Recovery entry: booking number + email instead of the emailed token link.
// The Stripe receipt carries the number, so a customer whose confirmation
// email never arrived can still reach their ticket. The response echoes
// manage_token so the SPA can continue with the token-based actions.
export function fetchBookingByNumber(departureNumber: string, email: string) {
  return manageCall<ManagePayload>({
    action: 'fetch',
    departure_number: departureNumber,
    email,
  });
}

// Re-send the confirmation email to the booking's stored address. The server
// enforces a cooldown (429 resend_cooldown) keyed on the last send.
export function resendTicketEmail(token: string) {
  return manageCall<{ sent: boolean }>({
    action: 'resend_email',
    manage_token: token,
  });
}

// Direct download links served by booking-ticket-pdf (GET + manage token).
// Plain hrefs on purpose: the browser handles the attachment download, no
// blob plumbing needed. The confirmation email no longer attaches the PDF
// and .ics (attachments hurt spam placement), so this page is where the
// customer comes to fetch them.
export function ticketPdfUrl(token: string): string {
  return `${API}/booking-ticket-pdf?t=${encodeURIComponent(token)}`;
}

export function ticketIcsUrl(token: string): string {
  return `${API}/booking-ticket-pdf?t=${encodeURIComponent(token)}&fmt=ics`;
}

// Betaallink-flow: start (of hervat) de online betaling van het openstaande
// bedrag op de parent booking. De server rekent het bedrag zelf uit en geeft
// een Stripe client_secret terug voor het Payment Element. 409 not_payable /
// nothing_due als er (inmiddels) niets meer open staat.
export interface BalancePaymentSession {
  client_secret: string;
  publishable_key: string;
  amount_due_cents: number;
  currency: string;
  booking_number: string;
}

export function startBalancePayment(token: string) {
  return manageCall<BalancePaymentSession>({
    action: 'start_balance_payment',
    manage_token: token,
  });
}

export function cancelBooking(token: string, reason?: string) {
  return manageCall<{ cancelled: boolean; booking_number: string }>({
    action: 'cancel',
    manage_token: token,
    reason,
  });
}

export interface NewSlotInput {
  room_id: string;
  slot_start: string;
  party_size: number;
}

export interface ReschedulePayload {
  rescheduled: boolean;
  booking_id: string;
  booking_number: string;
  old_slots: Array<{
    slot_index: number;
    room_id: string;
    slot_start: string;
    party_size: number;
  }>;
  new_slots: Array<{
    slot_index: number;
    room_id: string;
    slot_start: string;
    party_size: number;
  }>;
}

export function rescheduleBooking(
  token: string,
  newSlots: NewSlotInput[],
) {
  return manageCall<ReschedulePayload>({
    action: 'reschedule',
    manage_token: token,
    new_slots: newSlots,
  });
}

// Time-only move for multi-pick departures: every experience-pick shifts to
// new_slot_start, keeping its room + party size. The server resolves the rest.
export function rescheduleMultiBooking(
  token: string,
  newSlotStart: string,
) {
  return manageCall<ReschedulePayload>({
    action: 'reschedule_multi',
    manage_token: token,
    new_slot_start: newSlotStart,
  });
}

export async function fetchAvailability(
  experienceId: string,
  date: string,
  partySize: number,
  locale: string,
  // The booking's own location — scopes the slot list so a self-service
  // reschedule can only land at the same venue (the server enforces this too).
  locationId: string,
): Promise<AvailabilitySlot[]> {
  const res = await fetch(`${API}/booking-availability-search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: ANON,
      Authorization: `Bearer ${ANON}`,
    },
    body: JSON.stringify({
      experience_id: experienceId,
      date,
      party_size: partySize,
      locale,
      location_id: locationId,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as { slots: AvailabilitySlot[] };
  return json.slots ?? [];
}
